import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Sparkles, X } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { DishImage } from '@/components/DishImage';

/**
 * Celebratory pairing popup. Shown when the accompaniment wheel finishes spinning.
 * Renders the main dish + the chosen accompaniment side-by-side with confetti.
 */
export const PairingResultModal = ({ open, mainDish, accompaniment, onClose }) => {
  useEffect(() => {
    if (!open || !accompaniment) return;
    try {
      confetti({
        particleCount: 100,
        spread: 80,
        origin: { y: 0.55 },
        colors: ['#F97316', '#FBBF24', '#2D4A22', '#FFFFFF'],
        scalar: 0.9,
      });
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('PairingResultModal: confetti failed', err);
      }
    }
  }, [open, accompaniment]);

  if (!accompaniment) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-lg p-0 overflow-hidden bg-white border-none shadow-2xl rounded-3xl"
        data-testid="pairing-result-modal"
      >
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
              className="relative"
            >
              <button
                onClick={onClose}
                aria-label="Close pairing result"
                data-testid="pairing-result-close"
                className="absolute top-3 right-3 z-10 p-2 rounded-full bg-white/80 backdrop-blur hover:bg-white shadow-md"
              >
                <X className="w-4 h-4 text-[#1C1917]/70" />
              </button>

              <div className="bg-gradient-to-br from-[#FEF3E2] via-[#FFFFFF] to-[#FDFBF7] p-6 sm:p-8">
                <div className="flex items-center justify-center gap-1.5 mb-4">
                  <Sparkles className="w-3.5 h-3.5 text-[#F97316]" />
                  <span className="text-[10px] uppercase tracking-widest text-[#F97316] font-['Outfit'] font-semibold">
                    Pair your dish with
                  </span>
                  <Sparkles className="w-3.5 h-3.5 text-[#F97316]" />
                </div>

                <h3 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#1C1917] tracking-tight text-center mb-6">
                  {accompaniment.name}
                </h3>

                <div className="grid grid-cols-2 gap-4 mb-5">
                  {mainDish && (
                    <div className="text-center">
                      <div className="aspect-square rounded-2xl overflow-hidden bg-[#FDFBF7] border border-[#1C1917]/10 mb-2">
                        <DishImage
                          src={mainDish.image_url}
                          alt={mainDish.name}
                          fallbackInitial={mainDish.name?.[0]}
                          className="w-full h-full"
                          imgClassName="w-full h-full object-cover"
                        />
                      </div>
                      <p className="text-[11px] uppercase tracking-widest text-[#1C1917]/45 font-['Outfit'] font-semibold mb-0.5">
                        Main
                      </p>
                      <p className="font-['Playfair_Display'] font-bold text-sm text-[#1C1917] leading-tight line-clamp-2">
                        {mainDish.name}
                      </p>
                    </div>
                  )}

                  <div className="text-center">
                    <div className="aspect-square rounded-2xl overflow-hidden bg-[#FDFBF7] border border-[#F97316]/30 mb-2 ring-2 ring-[#F97316]/40">
                      <DishImage
                        src={accompaniment.image_url}
                        alt={accompaniment.name}
                        fallbackInitial={accompaniment.name?.[0]}
                        className="w-full h-full"
                        imgClassName="w-full h-full object-cover"
                      />
                    </div>
                    <p className="text-[11px] uppercase tracking-widest text-[#F97316] font-['Outfit'] font-semibold mb-0.5">
                      Pair with
                    </p>
                    <p className="font-['Playfair_Display'] font-bold text-sm text-[#1C1917] leading-tight line-clamp-2">
                      {accompaniment.name}
                    </p>
                  </div>
                </div>

                <p className="text-center text-xs text-[#1C1917]/55 font-['Outfit']">
                  {mainDish?.name
                    ? `A delicious match for ${mainDish.name}.`
                    : 'A delicious side to round out your meal.'}
                </p>

                <button
                  onClick={onClose}
                  data-testid="pairing-result-done-button"
                  className="mt-6 w-full py-3 rounded-full bg-[#2D4A22] hover:bg-[#1C1917] text-white font-['Outfit'] font-semibold transition-colors"
                >
                  Got it
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};
